import React, { useEffect, useState } from "react";
import axios from "axios";

// Manager backup for chalet allocation. Nothing is ever moved automatically: this panel only proposes
// moves (GET reassignment-suggestions) and lets the manager apply one booking at a time
// (POST {bookingId}/reassign-chalet), which the backend validates for the WHOLE stay.

export type PanelChaletType = "Big" | "Small";

export interface MovableBooking {
  bookingId: number;
  chaletId: number;
  customerName: string;
  arrivalText: string;
  departureText: string;
}

interface Suggestion {
  bookingId: number;
  orderNumber?: string;
  customerName?: string;
  arrivalDate: string;
  departureDate: string;
  fromChaletId: number;
  toChaletId: number;
  guestAlreadyArrived: boolean;
  description: string;
}

interface Props {
  apiUrl: string;
  bookings: MovableBooking[];
  stay: { type: PanelChaletType; arrival: string; departure: string } | null;
  onMoved: () => void;
}

const errorText = (e: any): string =>
  e?.response?.data?.message || e?.response?.data?.title || e?.message || "Något gick fel.";

const ChaletReassignPanel: React.FC<Props> = ({ apiUrl, bookings, stay, onMoved }) => {
  const [stayMessage, setStayMessage] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; text: string } | null>(null);
  const [refresh, setRefresh] = useState(0);

  const [manualBookingId, setManualBookingId] = useState<number | "">("");
  const [freeChalets, setFreeChalets] = useState<number[] | null>(null);
  const [targetChalet, setTargetChalet] = useState<number | "">("");

  const stayKey = stay ? `${stay.type}|${stay.arrival}|${stay.departure}` : "";

  useEffect(() => {
    if (!stay) {
      setSuggestions([]);
      setStayMessage(null);
      return;
    }
    let cancelled = false;
    axios
      .get<any>(`${apiUrl}/Booking/reassignment-suggestions`, {
        params: { chaletType: stay.type, startDate: stay.arrival, endDate: stay.departure },
      })
      .then((r) => {
        if (cancelled) return;
        setSuggestions(r.data?.suggestions ?? []);
        setStayMessage(r.data?.stay?.message ?? null);
      })
      .catch((e) => {
        if (!cancelled) {
          setSuggestions([]);
          setStayMessage(null);
          setFeedback({ ok: false, text: errorText(e) });
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, stayKey, refresh]);

  useEffect(() => {
    setFreeChalets(null);
    setTargetChalet("");
    if (manualBookingId === "") return;
    let cancelled = false;
    axios
      .get<any>(`${apiUrl}/Booking/${manualBookingId}/reassignment-options`)
      .then((r) => {
        if (!cancelled) setFreeChalets(r.data?.freeChaletIds ?? []);
      })
      .catch((e) => {
        if (!cancelled) setFeedback({ ok: false, text: errorText(e) });
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, manualBookingId, refresh]);

  const move = async (bookingId: number, toChaletId: number, fromChaletId: number | null, label: string) => {
    if (!window.confirm(label)) return;
    setBusy(true);
    setFeedback(null);
    try {
      const r = await axios.post<any>(`${apiUrl}/Booking/${bookingId}/reassign-chalet`, {
        chaletId: toChaletId,
        expectedCurrentChaletId: fromChaletId,
      });
      setFeedback({ ok: true, text: r.data?.message || "Bokningen flyttades." });
      setManualBookingId("");
      setRefresh((n) => n + 1);
      onMoved();
    } catch (e: any) {
      setFeedback({ ok: false, text: errorText(e) });
    } finally {
      setBusy(false);
    }
  };

  const sorted = [...bookings].sort((a, b) => a.chaletId - b.chaletId || a.arrivalText.localeCompare(b.arrivalText));
  const selected = sorted.find((b) => b.bookingId === manualBookingId);

  return (
    <div className="mb-3 p-2" style={{ border: "1px solid #ffe082", background: "#fffde7", borderRadius: 6 }}>
      <div className="mb-1">
        <strong>Flytta bokning till annan stuga</strong>{" "}
        <span className="text-muted">
          (reservlösning - inget flyttas automatiskt, hela vistelsen kontrolleras innan en bokning flyttas)
        </span>
      </div>

      {feedback && (
        <div className={`alert ${feedback.ok ? "alert-success" : "alert-danger"} py-1 px-2 mb-2`}>{feedback.text}</div>
      )}

      {stay && suggestions.length > 0 && (
        <div className="mb-2">
          <div className="mb-1">
            {stayMessage ||
              "Ingen enskild stuga är ledig hela perioden, men en bokning kan flyttas för att frigöra en stuga:"}
          </div>
          {suggestions.map((s) => (
            <div key={`${s.bookingId}-${s.toChaletId}`} className="d-flex align-items-center mb-1" style={{ gap: 8 }}>
              <span>
                Flytta bokning #{s.bookingId} ({s.customerName}, {s.arrivalDate.slice(0, 10)} → {s.departureDate.slice(0, 10)})
                från stuga {s.fromChaletId} till stuga {s.toChaletId}
                {s.guestAlreadyArrived && (
                  <strong style={{ color: "#b71c1c" }}> - gästen har redan anlänt!</strong>
                )}
              </span>
              <button
                className="btn btn-sm btn-warning"
                disabled={busy}
                onClick={() =>
                  move(
                    s.bookingId,
                    s.toChaletId,
                    s.fromChaletId,
                    `Flytta bokning #${s.bookingId} (${s.customerName}) från stuga ${s.fromChaletId} till stuga ${s.toChaletId}?\n\n` +
                      (s.guestAlreadyArrived ? "OBS: gästen har redan anlänt.\n\n" : "") +
                      "Datum och pris ändras inte."
                  )
                }
              >
                Flytta
              </button>
            </div>
          ))}
        </div>
      )}

      {stay && suggestions.length === 0 && stayMessage && <div className="mb-2 text-muted">{stayMessage}</div>}

      <div className="d-flex flex-wrap align-items-end" style={{ gap: 8 }}>
        <div>
          <label className="d-block mb-0">Bokning</label>
          <select
            className="form-control form-control-sm"
            value={manualBookingId}
            onChange={(e) => setManualBookingId(e.target.value ? Number(e.target.value) : "")}
          >
            <option value="">Välj bokning…</option>
            {sorted.map((b) => (
              <option key={b.bookingId} value={b.bookingId}>
                Stuga {b.chaletId}: {b.customerName} ({b.arrivalText} → {b.departureText}) #{b.bookingId}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="d-block mb-0">Flytta till</label>
          <select
            className="form-control form-control-sm"
            value={targetChalet}
            disabled={manualBookingId === "" || freeChalets === null}
            onChange={(e) => setTargetChalet(e.target.value ? Number(e.target.value) : "")}
          >
            <option value="">
              {freeChalets !== null && freeChalets.length === 0 ? "Ingen annan stuga ledig" : "Välj stuga…"}
            </option>
            {(freeChalets ?? []).map((id) => (
              <option key={id} value={id}>
                Stuga {id} (ledig hela vistelsen)
              </option>
            ))}
          </select>
        </div>
        <button
          className="btn btn-sm btn-warning"
          disabled={busy || !selected || targetChalet === ""}
          onClick={() =>
            selected &&
            targetChalet !== "" &&
            move(
              selected.bookingId,
              targetChalet,
              selected.chaletId,
              `Flytta bokning #${selected.bookingId} (${selected.customerName}) från stuga ${selected.chaletId} till stuga ${targetChalet}?`
            )
          }
        >
          Flytta bokning
        </button>
      </div>
    </div>
  );
};

export default ChaletReassignPanel;
